import Keycloak from 'keycloak-js';
import configs from '../configs';
import {
  getKeycloakConfig,
  KEYCLOAK_REALM_STORAGE_KEY,
  KeycloakRealm,
  getRedirectUri as resolveRedirectUri,
} from './keycloakConfig';

/**
 * Keycloak Instance Management — Singleton Pattern
 *
 * Responsibilities:
 * - Create and hold a single Keycloak instance
 * - Manage initialization, login, logout, token refresh
 * - Register Keycloak event callbacks (onAuthSuccess / onTokenExpired / onAuthRefreshSuccess / onAuthLogout / onAuthError)
 * - Notify the React layer of auth state changes via pub/sub pattern
 * - Only store realm selection in localStorage; token lifecycle is managed by keycloak-js
 *
 * Reference: Keycloak 26.x frontend integration best practices — do NOT store access_token or refresh_token in localStorage/sessionStorage
 */

let keycloakInstance = null;
let initPromise = null;
let eventHandlersRegistered = false;

// In-flight auth code guard — prevents the same authorization code from being
// submitted to the token endpoint twice. Authorization codes are single-use:
// a second POST with the same code returns 400 invalid_grant.
// This can happen when:
// - React re-invokes useEffect (HMR, route change) while the first fetch is still pending
// - The browser back/forward button replays the callback URL
// - sessionStorage dedup guard hasn't been set yet (race window)
let authCodeInFlight = null;

// Token refresh interval ID — stored so it can be cleared on logout / reset.
// Without this, the interval keeps running after logout and sends refresh
// requests with stale tokens, causing 400 errors on the token endpoint.
let tokenRefreshIntervalId = null;

// Flag to stop token refresh after a failure — prevents endless retry loops
// when the refresh token is expired or the session has been invalidated.
let tokenRefreshStopped = false;

// ─── Auth State Pub/Sub ───────────────────────────────────────────
const authStateListeners = new Set();
const tokenListeners = new Set();

/**
 * Subscribe to auth state changes
 * @param {(authenticated: boolean) => void} callback
 * @returns {() => void} Unsubscribe function
 */
export function onAuthStateChange(callback) {
  authStateListeners.add(callback);
  return () => authStateListeners.delete(callback);
}

/**
 * Subscribe to token changes (notify HTTP layer to get latest token after refresh)
 * @param {() => void} callback
 * @returns {() => void} Unsubscribe function
 */
export function onTokenChange(callback) {
  tokenListeners.add(callback);
  return () => tokenListeners.delete(callback);
}

function notifyAuthStateChange(authenticated) {
  authStateListeners.forEach(cb => {
    try {
      cb(authenticated);
    } catch (e) {
      console.error('Auth state listener error:', e);
    }
  });
}

function notifyTokenChange() {
  tokenListeners.forEach(cb => {
    try {
      cb();
    } catch (e) {
      console.error('Token listener error:', e);
    }
  });
}

// ─── Keycloak Instance Management ──────────────────────────────────

/**
 * Check if Keycloak is the currently active authentication method
 * @returns {boolean}
 */
export function isKeycloakEnabled() {
  return configs.auth_method === 'keycloak';
}

/**
 * Health check — probe Keycloak server reachability with a timeout
 *
 * Sends a lightweight HEAD request to the Keycloak server's realm info endpoint.
 * If the server responds within the timeout, Keycloak is considered available.
 * Used for automatic fallback to MSAL/Auth2.0 when Keycloak is down.
 *
 * @param {number} [timeoutMs=5000] - Timeout in milliseconds
 * @returns {Promise<boolean>} true if Keycloak is reachable, false otherwise
 */
export async function checkKeycloakHealth(timeoutMs = 5000) {
  const realm =
    localStorage.getItem(KEYCLOAK_REALM_STORAGE_KEY) || KeycloakRealm.Internal;
  const kcConfig = getKeycloakConfig(realm);

  const healthUrl = `${kcConfig.url}/realms/${kcConfig.realm}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(healthUrl, {
      method: 'GET',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    clearTimeout(timeoutId);
    console.warn(
      'Keycloak health check failed, will fall back to MSAL:',
      error?.message || error,
    );
    return false;
  }
}

/**
 * Get the Keycloak instance (singleton), creating it if it doesn't exist yet
 * Realm is read from localStorage to ensure the same realm is used after redirect
 */
export function getKeycloakInstance() {
  if (!keycloakInstance) {
    const realm =
      localStorage.getItem(KEYCLOAK_REALM_STORAGE_KEY) ||
      KeycloakRealm.Internal;
    const config = getKeycloakConfig(realm);
    keycloakInstance = new Keycloak(config);
    // Expose to window for external token access
    window.__keycloak = keycloakInstance;
  }
  return keycloakInstance;
}

// ─── Keycloak Event Callback Registration ────────────────────────

/**
 * Register Keycloak event callbacks
 * Corresponds to Angular spec's AuthenticationBootstrapService.registerKeycloakEventHandlers()
 *
 * Events:
 * - onAuthSuccess: Notify React layer after successful authentication
 * - onTokenExpired: Attempt token refresh when token expires
 * - onAuthRefreshSuccess: Notify HTTP layer to get latest token after successful refresh
 * - onAuthLogout: Clean up state when user logs out
 * - onAuthError: Force logout on auth/refresh failure
 */
function registerKeycloakEventHandlers(keycloak) {
  if (eventHandlersRegistered) {
    return;
  }
  eventHandlersRegistered = true;

  // Auth success
  keycloak.onAuthSuccess = () => {
    notifyAuthStateChange(true);
  };

  // Token expired — attempt refresh
  keycloak.onTokenExpired = () => {
    if (tokenRefreshStopped) return;
    keycloak
      .updateToken(30)
      .then(refreshed => {
        if (refreshed) {
          // Keep kc_id_token in sync with the rotated id_token so a logout
          // after a page refresh sends a valid id_token_hint (not the stale
          // one from login time → KC logout 400).
          syncIdTokenToStorage(keycloak);
          notifyTokenChange();
        }
      })
      .catch(() => {
        console.warn('Token refresh failed on expiry, session may expire soon');
        tokenRefreshStopped = true;
      });
  };

  // Token refresh success
  keycloak.onAuthRefreshSuccess = () => {
    // Fires after every successful updateToken(). Keep kc_id_token in sync
    // with the rotated id_token — logoutKeycloak() reads it as the
    // id_token_hint fallback after a page refresh.
    syncIdTokenToStorage(keycloak);
    notifyTokenChange();
  };

  // User logout — clean up app state
  keycloak.onAuthLogout = () => {
    notifyAuthStateChange(false);
  };

  // Auth/refresh error — log error but don't force logout (for debugging)
  keycloak.onAuthError = error => {
    console.error('Keycloak auth error:', error);
    console.warn('Auth error details:', {
      message: error?.message || error,
      type: error?.type,
    });
    // In dev environment, don't force logout to allow users to see error details
    // In production, consider forcing logout
    if (configs.env === 'prod') {
      keycloak.logout({
        redirectUri: window.location.origin,
      });
    }
  };
}

// ─── Initialization ─────────────────────────────────────────────

/** Keycloak init timeout (ms) — prevents infinite wait when server is unreachable.
 *  Reduced to 3s: the callback flow now uses exchangeAuthCodeForToken()
 *  (direct POST to token endpoint) instead of keycloak.init(), so initKeycloak()
 *  is only used for the silent check-sso flow where 3s is sufficient. */
const KEYCLOAK_INIT_TIMEOUT_MS = 10000;

/**
 * Decode a JWT token's payload (without verification — for reading claims only)
 * @param {string} jwt - The JWT token string
 * @returns {Object|null} The decoded payload object, or null if decoding fails
 */
function decodeJwtPayload(jwt) {
  if (!jwt || typeof jwt !== 'string') return null;
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(payload)));
  } catch (e) {
    console.error('Failed to decode JWT payload:', e);
    return null;
  }
}

/**
 * Check whether an id_token can be safely sent as id_token_hint to KC's
 * logout endpoint.
 *
 * KC 26.x returns HTTP 400 for the logout endpoint when id_token_hint is
 * malformed or expired. That leaves the browser stuck on KC's error page AND
 * the KC session alive (not destroyed) — the surviving session then breaks
 * the next login (silent re-auth against a half-logged-out session can issue
 * codes that fail at the token endpoint with 400 invalid_grant).
 *
 * The logout endpoint still works without id_token_hint: it identifies the
 * session via the browser's KC cookies and honors post_logout_redirect_uri.
 *
 * @param {string} idToken - The id_token candidate for id_token_hint
 * @returns {boolean} true only when the token parses and is not yet expired
 */
function isIdTokenHintUsable(idToken) {
  if (!idToken || typeof idToken !== 'string') {
    return false;
  }
  const payload = decodeJwtPayload(idToken);
  if (!payload || !payload.exp) {
    return false;
  }
  // Allow 30s clock skew between client and server
  return payload.exp * 1000 > Date.now() - 30 * 1000;
}

/**
 * Keep sessionStorage's kc_id_token in sync with the Keycloak instance's
 * current id_token.
 *
 * logoutKeycloak() reads kc_id_token as a fallback when the in-memory
 * instance has no tokens (e.g., after a page refresh / browser restore).
 * keycloak-js rotates the id_token on every successful updateToken(), but
 * without syncing here the stored copy stays stale — the original token from
 * login time, long expired after a lengthy session. A stale copy would be
 * sent as id_token_hint on logout → KC 400.
 *
 * @param {Object} keycloak - The Keycloak instance holding the fresh tokens
 */
function syncIdTokenToStorage(keycloak) {
  if (keycloak && keycloak.idToken) {
    sessionStorage.setItem('kc_id_token', keycloak.idToken);
  } else {
    sessionStorage.removeItem('kc_id_token');
  }
}

/**
 * Manually exchange the authorization code for Keycloak tokens.
 *
 * This bypasses keycloak.init() entirely to avoid the "3rd party check iframe"
 * timeout that occurs when Keycloak JS tries to use a hidden iframe for SSO
 * session checking in cross-origin environments (e.g., QA/PROD).
 *
 * Flow:
 * 1. Parse the auth code from the URL (query params or fragment)
 * 2. POST to the Keycloak token endpoint with grant_type=authorization_code
 * 3. Set the tokens on the Keycloak instance
 * 4. Register token refresh and notify auth state listeners
 *
 * @returns {Promise<boolean>} Always returns true (throws on failure)
 * @throws {Error} If no auth code is found, or the token exchange request fails
 */
export async function exchangeAuthCodeForToken() {
  const keycloak = getKeycloakInstance();
  registerKeycloakEventHandlers(keycloak);

  // Parse auth code from URL — supports both query and fragment response modes
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(
    window.location.hash.replace(/^#/, ''),
  );
  const code = urlParams.get('code') || hashParams.get('code');

  if (!code) {
    throw new Error('No authorization code found in URL');
  }

  // In-flight guard: if this exact code is already being exchanged (e.g.,
  // React double-invoked useEffect, or the component remounted during the
  // fetch), return the existing promise instead of sending a duplicate POST.
  // Authorization codes are single-use — a second POST returns 400.
  if (authCodeInFlight === code) {
    // Return a resolved promise using the existing instance state.
    // The first caller's exchange will have set the tokens by now (or will
    // soon). If tokens aren't set yet, the caller will throw on the next check.
    return keycloak.authenticated || false;
  }
  authCodeInFlight = code;

  // Clean the auth code from the URL IMMEDIATELY after reading it, before the
  // fetch. Authorization codes are single-use: if the page is refreshed or the
  // component remounts while the token exchange is in flight, the same code
  // would be POSTed again, causing Keycloak to return 400 invalid_grant.
  // By removing it from the URL now, a remount won't find a code to resend.
  window.history.replaceState({}, document.title, window.location.pathname);

  // Build the token endpoint URL using the selected realm
  const realm =
    localStorage.getItem(KEYCLOAK_REALM_STORAGE_KEY) || KeycloakRealm.Internal;
  const kcConfig = getKeycloakConfig(realm);

  // Redirect URI must match the one used in the authorization request
  const redirectUri = resolveRedirectUri();

  // Exchange the authorization code for tokens via the Keycloak token endpoint
  const tokenResponse = await fetch(
    `${kcConfig.url}/realms/${kcConfig.realm}/protocol/openid-connect/token`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        client_id: kcConfig.clientId,
        redirect_uri: redirectUri,
      }),
    },
  );

  if (!tokenResponse.ok) {
    const errorText = await tokenResponse.text();
    const error = new Error(
      `Token exchange failed: ${tokenResponse.status} ${errorText}`,
    );
    // Mark retryable errors — "Code not valid" (expired/already-consumed
    // authorization code). LoginKeycloak catches isRetryable and silently
    // redirects to KC for a fresh code instead of showing a dead-end error.
    if (
      tokenResponse.status === 400 &&
      (errorText.includes('Code not valid') ||
        errorText.includes('invalid_grant'))
    ) {
      error.isRetryable = true;
    }
    throw error;
  }

  const tokens = await tokenResponse.json();

  // Set tokens on the Keycloak instance so that keycloak.token, keycloak.tokenParsed,
  // keycloak.authenticated, etc. are available for the rest of the application
  keycloak.token = tokens.access_token;
  keycloak.refreshToken = tokens.refresh_token || null;
  keycloak.idToken = tokens.id_token || null;
  keycloak.tokenParsed = decodeJwtPayload(tokens.access_token);
  keycloak.authenticated = true;

  // Sync the fresh id_token to sessionStorage right away. logoutKeycloak()
  // reads it as the id_token_hint fallback after a page refresh, and
  // keycloak-js rotates the id_token on every updateToken() — without this
  // sync the stored copy would go stale and a later logout would send an
  // expired hint → KC logout 400 → session left alive → next login broken.
  syncIdTokenToStorage(keycloak);

  // Calculate time skew between client and server (used for token expiry checks)
  if (keycloak.tokenParsed && keycloak.tokenParsed.iat) {
    keycloak.timeSkew =
      Math.floor(Date.now() / 1000) - keycloak.tokenParsed.iat;
  }

  // Register periodic token refresh and notify listeners
  registerTokenRefresh(keycloak);
  notifyAuthStateChange(true);

  return true;
}

/**
 * Initialize Keycloak (idempotent)
 *
 * Used for both:
 * 1. Silent check-sso flow (no auth code in URL) — checks SSO status via hidden iframe
 * 2. Auth code callback (auth code in URL) — keycloak.init() detects the code and
 *    automatically exchanges it for tokens via the Keycloak token endpoint.
 *    CORS is enabled via Keycloak's webOrigins configuration.
 *    This is the same approach used by the omnia project's KeycloakProvider.
 *
 * Built-in timeout protection: When the Keycloak server is unreachable
 * (e.g., DNS resolution failure, network down), keycloak.init()'s Promise
 * may neither resolve nor reject for a long time, leaving the page stuck
 * in a loading state. After timeout, treat as unauthenticated and render
 * the login page.
 *
 * @param {Object} initOptions - Optional, override default init options
 * @returns {Promise<boolean>} Whether authenticated
 */
export function initKeycloak(initOptions) {
  if (initPromise) {
    return initPromise;
  }

  const keycloak = getKeycloakInstance();
  registerKeycloakEventHandlers(keycloak);

  // Redirect URI for the OAuth2 flow
  const redirectUri = resolveRedirectUri();

  const defaultOptions = {
    onLoad: 'check-sso',
    redirectUri: redirectUri,
    checkLoginIframe: false,
    // Keep check-sso inside the hidden callback iframe. Without this callback,
    // keycloak-js falls back to a top-level prompt=none redirect and returns
    // to the app with #error=login_required when no SSO session exists.
    silentCheckSsoRedirectUri: `${redirectUri}/silent-check-sso.html`,
    // A blocked third-party-cookie check should resolve as unauthenticated;
    // never fall back to a visible redirect/error page.
    silentCheckSsoFallback: false,
    messageReceiveTimeout: 5000,
    // pkceMethod intentionally omitted: buildKeycloakLoginUrl() manually constructs
    // the authorization URL without a code_challenge. Setting pkceMethod: 'S256' here
    // would cause a code_verifier mismatch during token exchange.
  };

  const options = { ...defaultOptions, ...initOptions };

  // Timeout protection: race against keycloak.init() to prevent infinite hang when server is unreachable
  const initWithTimeout = Promise.race([
    keycloak.init(options),
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error('Keycloak init timeout')),
        KEYCLOAK_INIT_TIMEOUT_MS,
      ),
    ),
  ]);

  initPromise = initWithTimeout
    .then(authenticated => {
      registerTokenRefresh(keycloak);
      return authenticated;
    })
    .catch(error => {
      // HMR scenario: singleton variables are reset after module reload, but the Keycloak instance
      // remains in memory. init() will throw "A 'Keycloak' instance can only be initialized once".
      // Fallback: use the existing instance's auth state without blocking app startup
      const isAlreadyInitialized =
        error &&
        error.message &&
        error.message.includes('only be initialized once');

      if (isAlreadyInitialized) {
        console.warn(
          'Keycloak instance was already initialized (likely HMR), using existing state',
        );
        return keycloak.authenticated || false;
      }

      // Timeout or network error: don't block app startup, let user see the login page
      console.error('Keycloak init failed:', error);
      initPromise = null;
      throw error;
    });

  return initPromise;
}

/**
 * Register automatic token refresh
 * Attempts refresh 30 seconds before token expiry
 *
 * The interval ID is stored in a module variable so it can be cleared by
 * resetKeycloakInstance() / logoutKeycloak() — otherwise the interval keeps
 * running after logout and sends refresh requests with stale tokens → 400.
 */
export function registerTokenRefresh(keycloak) {
  const MIN_VALIDITY_SECONDS = 30;
  const CHECK_INTERVAL_MS = 60000;

  // Clear any previous interval (e.g., from a prior login session)
  if (tokenRefreshIntervalId !== null) {
    clearInterval(tokenRefreshIntervalId);
  }
  // Reset the stopped flag for a new session
  tokenRefreshStopped = false;

  tokenRefreshIntervalId = setInterval(() => {
    // Guard: stop if the instance has been reset (logout / session expired)
    if (keycloakInstance !== keycloak || tokenRefreshStopped) {
      return;
    }
    if (keycloak.authenticated) {
      keycloak
        .updateToken(MIN_VALIDITY_SECONDS)
        .then(refreshed => {
          if (refreshed) {
            // Keep kc_id_token in sync with the rotated id_token (the
            // scheduled refresh also triggers onAuthRefreshSuccess, but sync
            // here as well to be resilient across keycloak-js versions).
            syncIdTokenToStorage(keycloak);
            notifyTokenChange();
          }
        })
        .catch(() => {
          console.warn('Token refresh failed, session may expire');
          // Stop further attempts — refresh token is likely expired.
          // Continuing would send stale refresh tokens → 400 on every tick.
          tokenRefreshStopped = true;
        });
    }
  }, CHECK_INTERVAL_MS);
}

// ─── Login ───────────────────────────────────────────────────────

/**
 * Generate a random state parameter for OAuth2 CSRF protection
 * @returns {string}
 */
function generateRandomState() {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Build the Keycloak OAuth2 authorization URL
 *
 * Does not rely on the Keycloak JS instance's login() method (which requires init() first),
 * but directly constructs the OIDC authorization endpoint URL.
 *
 * Uses response_mode=fragment (consistent with the Omnia project):
 * - Authorization code is returned via URL fragment (#) instead of query params
 * - Prevents authorization code from appearing in server/proxy logs
 * - keycloak-js init() will automatically parse callback params from the fragment
 *
 * Internal users trigger the SAML broker flow via kc_idp_hint=saml,
 * Keycloak will automatically redirect internally to /realms/{realm}/broker/saml/login
 *
 * @param {string} realmKey - KeycloakRealm.Internal or KeycloakRealm.External
 * @param {Object} [options] - Additional options
 * @param {string} [options.idpHint] - Identity Provider hint (e.g., 'saml')
 * @returns {{ url: string, state: string }} Authorization URL and state (for CSRF verification)
 */
export function buildKeycloakLoginUrl(realmKey, options = {}) {
  const config = getKeycloakConfig(realmKey);

  const redirectUri = resolveRedirectUri();

  const state = generateRandomState();

  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid profile email',
    state: state,
  });

  if (options.idpHint) {
    params.set('kc_idp_hint', options.idpHint);
  }

  if (options.prompt) {
    params.set('prompt', options.prompt);
  }

  const loginUrl = `${config.url}/realms/${
    config.realm
  }/protocol/openid-connect/auth?${params.toString()}`;
  return { url: loginUrl, state };
}

/**
 * Build the Keycloak OAuth2 authorization URL and redirect the browser
 *
 * @param {string} realmKey - KeycloakRealm.Internal or KeycloakRealm.External
 * @param {Object} [options] - Additional options
 * @param {string} [options.idpHint] - Identity Provider hint (e.g., 'saml')
 */
function redirectToKeycloakLogin(realmKey, options = {}) {
  localStorage.setItem(KEYCLOAK_REALM_STORAGE_KEY, realmKey);

  const { url, state } = buildKeycloakLoginUrl(realmKey, options);

  // Store state for callback verification
  sessionStorage.setItem('kc_oauth_state', state);
  window.location.href = url;
}

/**
 * Unified login — redirects to Keycloak's native login page
 *
 * Uses the Ambev realm WITHOUT kc_idp_hint, so Keycloak displays its own
 * login page with all configured Identity Providers (SAML SSO + username/password).
 * The user selects their login method on the Keycloak page itself.
 *
 * This approach requires Keycloak to have Identity Providers configured in the
 * Ambev realm (SAML IdP for internal SSO + standard login for external users).
 *
 * @param {string} [realmKey] - Optional realm override (defaults to internal/Ambev)
 */
export async function loginWithKeycloak(realmKey) {
  redirectToKeycloakLogin(realmKey || KeycloakRealm.Internal);
}

/**
 * Check an existing Keycloak SSO session without showing the login form.
 * A top-level redirect is more reliable on Safari/iPad than a hidden iframe,
 * because the redirect runs in a first-party cookie context.
 */
export async function loginSilentlyWithKeycloak() {
  const realm =
    localStorage.getItem(KEYCLOAK_REALM_STORAGE_KEY) || KeycloakRealm.Internal;
  const options = { prompt: 'none' };
  if (realm === KeycloakRealm.Internal) {
    options.idpHint = 'saml';
  }
  redirectToKeycloakLogin(realm, options);
}

/**
 * Internal user login — uses SAML idpHint
 * 1. Write realm to localStorage (bootstrap reads the same realm after redirect)
 * 2. Directly construct OAuth2 authorization URL and redirect to Keycloak login page (no Keycloak JS instance dependency)
 */
export async function loginAsInternalUser() {
  redirectToKeycloakLogin(KeycloakRealm.Internal, { idpHint: 'saml' });
}

/**
 * External user login — standard Keycloak login
 */
export async function loginAsExternalUser() {
  redirectToKeycloakLogin(KeycloakRealm.External);
}

/**
 * Retry login with a fresh authorization code.
 *
 * Called when a previous code exchange failed with "Code not valid" — the
 * authorization code expired (slow network: main bundle + lazy chunk download
 * between KC's redirect and LoginKeycloak's useEffect can exceed the code's
 * 60s lifetime) or was already consumed.
 *
 * Redirects to KC's authorization endpoint using the same realm and idp hint
 * as the original login. Because the user's KC session (or the SAML broker's
 * session) is typically still active, a fresh code is issued silently — the
 * user sees a brief redirect chain but no re-prompt for credentials.
 *
 * Callers must limit retries (LoginKeycloak uses sessionStorage counter
 * `kc_auth_retry_count`, max 1) to prevent redirect loops.
 */
export function retryWithFreshCode() {
  const realm =
    localStorage.getItem(KEYCLOAK_REALM_STORAGE_KEY) || KeycloakRealm.Internal;

  if (realm === KeycloakRealm.External) {
    redirectToKeycloakLogin(KeycloakRealm.External);
  } else {
    redirectToKeycloakLogin(KeycloakRealm.Internal, { idpHint: 'saml' });
  }
}

// ─── Logout ───────────────────────────────────────────────────────

/**
 * Logout from Keycloak
 *
 * Always constructs and redirects to the Keycloak logout endpoint, regardless
 * of whether the in-memory Keycloak instance has auth state. This is critical
 * because the Keycloak instance is lost on page refresh / HMR, but we still
 * need to terminate the KC session.
 *
 * The `id_token_hint` parameter is included (from sessionStorage or the KC
 * instance) only when it parses as a JWT and is not yet expired — KC 26.x
 * returns HTTP 400 for the logout endpoint when handed an invalid/expired
 * hint, which leaves the KC session alive and breaks the next login. Without
 * a hint KC still ends the session (identified via browser cookies) and
 * redirects back to post_logout_redirect_uri.
 *
 * Sets `kc_logout_pending` in sessionStorage so that LoginKeycloak can chain
 * a Microsoft logout redirect after the browser returns from KC logout.
 */
export async function logoutKeycloak() {
  const keycloak = getKeycloakInstance();
  const redirectUri = resolveRedirectUri();

  // Retrieve id_token for id_token_hint — prefer the KC instance, fall back
  // to sessionStorage (stored during login, survives page refresh).
  const idToken =
    keycloak.idToken || sessionStorage.getItem('kc_id_token') || null;

  // Always set the pending flag — LoginKeycloak checks this after the
  // browser returns from KC logout to decide whether to chain MS logout.
  sessionStorage.setItem('kc_logout_pending', 'true');

  // Build the Keycloak logout URL manually from config. This works even when
  // the KC instance was never initialized (e.g., after page refresh).
  const realm =
    localStorage.getItem(KEYCLOAK_REALM_STORAGE_KEY) || KeycloakRealm.Internal;
  const kcConfig = getKeycloakConfig(realm);

  const params = new URLSearchParams({
    post_logout_redirect_uri: redirectUri,
    client_id: kcConfig.clientId,
  });

  if (idToken && isIdTokenHintUsable(idToken)) {
    params.set('id_token_hint', idToken);
  }

  const logoutUrl = `${kcConfig.url}/realms/${
    kcConfig.realm
  }/protocol/openid-connect/logout?${params.toString()}`;

  localStorage.removeItem(KEYCLOAK_REALM_STORAGE_KEY);
  // Clean up id_token from sessionStorage (no longer needed after logout URL is built)
  sessionStorage.removeItem('kc_id_token');
  notifyAuthStateChange(false);
  resetKeycloakInstance();

  // Redirect browser to Keycloak logout endpoint — this navigates away
  // from the app, so code after this line will not execute.
  window.location.href = logoutUrl;
}

// ─── Token Utilities ─────────────────────────────────────────────

/**
 * Get the current Keycloak bearer token
 * Token lifecycle is managed by keycloak-js, exposed to HTTP layer in memory only
 * @returns {string|null} access_token or null
 */
export function getBearerToken() {
  if (!keycloakInstance) {
    return null;
  }
  if (keycloakInstance.authenticated && keycloakInstance.token) {
    return keycloakInstance.token;
  }
  return null;
}

/**
 * Get the Authorization header value for API requests
 * - When Keycloak is active: returns "Bearer <kc_token>"
 * - Otherwise: returns the java-token from sessionStorage (backward compatible with MSAL flow)
 * @returns {string}
 */
export function getAuthToken() {
  const kcToken = getBearerToken();
  if (kcToken) {
    return `Bearer ${kcToken}`;
  }
  return sessionStorage.getItem('java-token');
}

// ─── Role Checks ─────────────────────────────────────────────────

/**
 * Check if the user has the specified role (resource role)
 * @param {string} role - Role name
 * @returns {boolean}
 */
export function hasRole(role) {
  if (!keycloakInstance) {
    return false;
  }
  if (!keycloakInstance.authenticated || !keycloakInstance.tokenParsed) {
    return false;
  }
  return keycloakInstance.hasResourceRole(role);
}

/**
 * Check if the user has any of the specified roles
 * @param {string[]} roles - Role list
 * @returns {boolean}
 */
export function hasAnyRole(roles) {
  if (!roles || roles.length === 0) {
    return true;
  }
  return roles.some(role => hasRole(role));
}

// ─── Instance Management ─────────────────────────────────────────

/**
 * Reset the Keycloak instance (used after realm switch or logout)
 * Clears singleton and init promise; next call to getKeycloakInstance will recreate
 */
export function resetKeycloakInstance() {
  // Clear the token refresh interval — prevents stale refresh requests
  // after logout or session expiry, which cause 400 errors on the token endpoint.
  if (tokenRefreshIntervalId !== null) {
    clearInterval(tokenRefreshIntervalId);
    tokenRefreshIntervalId = null;
  }
  tokenRefreshStopped = false;
  initPromise = null;
  keycloakInstance = null;
  eventHandlersRegistered = false;
  authCodeInFlight = null;
}

/**
 * Get user info from Keycloak tokenParsed
 * @returns {Object|null}
 */
export function getKeycloakUser() {
  if (!keycloakInstance || !keycloakInstance.authenticated) {
    return null;
  }
  return keycloakInstance.tokenParsed;
}

/**
 * Convenience function: Get Keycloak Access Token
 * Can be called directly from console: window.getKcToken()
 * @returns {string|null}
 */
export function getKcToken() {
  if (!keycloakInstance || !keycloakInstance.authenticated) {
    console.warn('Keycloak not authenticated');
    return null;
  }
  return keycloakInstance.token;
}

/**
 * Convenience function: Get user info (parsed Token)
 * Can be called directly from console: window.getKcUserInfo()
 * @returns {Object|null}
 */
export function getKcUserInfo() {
  if (!keycloakInstance || !keycloakInstance.authenticated) {
    console.warn('Keycloak not authenticated');
    return null;
  }
  const token = keycloakInstance.tokenParsed;
  return {
    sub: token.sub,
    email: token.email,
    name: token.name,
    preferred_username: token.preferred_username,
    roles: token.realm_access?.roles || [],
    exp: token.exp,
    iat: token.iat,
  };
}

// Expose to window for debugging
/* istanbul ignore else -- window always exists in browser/jsdom test env */
if (typeof window !== 'undefined') {
  window.getKcToken = getKcToken;
  window.getKcUserInfo = getKcUserInfo;
}
